import { useComparisonStats, type ComparisonGroup } from "../../../../shared/hooks";
import type { MetricsFilters } from "../../../../shared/api/types";
import type { ComparisonSummaryRequest } from "../api";

export interface AiSummaryMetricSpec {
  /** Display label shown to the model, e.g. "Recovery". */
  label: string;
  /** Stat keys to try in order (first present wins), e.g. ["recovery","recovery_score"]. */
  keys: string[];
}

interface UseAiSummaryArgs {
  dashboard: string;
  groups: ComparisonGroup[];
  isComparing: boolean;
  dimension?: string | null;
  filterParams: MetricsFilters;
  /** Headline metrics to include in the summary, in display order. */
  metricSpecs: AiSummaryMetricSpec[];
}

/**
 * Shared plumbing for the on-demand AI summary: fetches each group's headline
 * stats and produces a lazy payload builder + disabled flag. Keeps per-dashboard
 * wiring to a few lines.
 */
export const useAiSummary = ({
  dashboard,
  groups,
  isComparing,
  dimension,
  filterParams,
  metricSpecs,
}: UseAiSummaryArgs) => {
  const allKeys = Array.from(new Set(metricSpecs.flatMap((m) => m.keys)));
  const comparisonStats = useComparisonStats(groups, allKeys);

  const buildPayload = (): ComparisonSummaryRequest => {
    const aiGroups = comparisonStats.map((entry) => {
      const d = (entry.data?.data ?? {}) as Record<
        string,
        { value?: number | null; unit?: string | null; rag_label?: string | null; change?: string | null }
      >;
      const metrics = metricSpecs
        .map((spec) => {
          const key = spec.keys.find((k) => d[k]);
          const stat = key ? d[key] : undefined;
          if (!stat) return null;
          return {
            label: spec.label,
            value: typeof stat.value === "number" ? stat.value : null,
            unit: stat.unit ?? null,
            rag_label: stat.rag_label ?? null,
            change: stat.change ?? null,
          };
        })
        .filter((m): m is NonNullable<typeof m> => m !== null);
      return { label: entry.group.label, metrics };
    });

    return {
      dashboard,
      mode: isComparing ? "comparison" : "average",
      dimension: isComparing ? dimension ?? null : null,
      period: {
        start_date: filterParams.start_date,
        end_date: filterParams.end_date,
      },
      groups: aiGroups,
    };
  };

  return {
    buildPayload,
    disabled: comparisonStats.some((e) => e.isLoading),
  };
};
